import { Link } from '@tanstack/react-router';
import { Input } from '@/components/atoms/input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/atoms/select';
import { QbAccountPicker, QbClassPicker } from '@/components/settings/accounting/QbAccountPicker';
import { useChartOfAccounts } from '@/hooks/settings/useChartOfAccounts';
import { useChartOfClasses } from '@/hooks/settings/useChartOfClasses';
import { useQuickBooksStatus } from '@/hooks/settings/useQuickBooksStatus';
import {
  AUTO_TAX_LINE_TYPE,
  OVERRIDABLE_TAX_LINE_TYPES,
  resolveAutoTaxLineType,
  selectableTaxLineType,
  taxLineTypeLabel,
} from '@/lib/sales/tax-line-type';

export interface LineTaxAccounting {
  taxLineType?: string | null;
  taxCategory?: string | null;
  taxCode?: string | null;
  glAccount?: string | null;
  glCogs?: string | null;
  glExpenseAccount?: string | null;
  glClass?: string | null;
}

interface LineTaxAccountingFieldsProps {
  /** The line's item type, so "Auto" can name what it would resolve to. */
  itemType: string;
  taxable: boolean;
  value: LineTaxAccounting;
  onChange: (patch: LineTaxAccounting) => void;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  );
}

/**
 * How a hand-typed document line is classified for tax, and where its money
 * posts — the same four answers the pricebook item form collects, on the one
 * kind of line that has no catalog row to inherit them from.
 *
 * A line picked from the catalog arrives with all of this already snapshotted,
 * so it is deliberately not re-asked there: the pricebook is where an item's
 * class and accounts are decided, and offering the same four fields per
 * estimate would invite the two to disagree with no way to tell which was
 * meant. A custom line has no such source, and left unasked it reached the
 * ledger classified by a guess from its bare type and posted to no account.
 */
export function LineTaxAccountingFields({
  itemType,
  taxable,
  value,
  onChange,
}: LineTaxAccountingFieldsProps) {
  // Gated on a live QBO connection, exactly as the pricebook form is: an
  // account id typed against no chart of accounts is a string that resolves to
  // nothing on the other side.
  const { data: qbStatus } = useQuickBooksStatus();
  const qbConnected = !!qbStatus?.connected;
  const { data: qbAccounts = [] } = useChartOfAccounts({ enabled: qbConnected });
  const { data: qbClasses = [] } = useChartOfClasses({ enabled: qbConnected });

  const autoLabel = taxLineTypeLabel(resolveAutoTaxLineType(itemType, taxable));

  // A stored class the engine derives over reads as AUTO, because that is
  // exactly what happens to it: a line snapshotted `materials` from the catalog
  // is still classified by what the line IS. Showing it as its stored value
  // would promise an override that nothing honours.
  const selectedTaxLineType = selectableTaxLineType(value.taxLineType);

  return (
    <div className="space-y-3 rounded-md border border-dashed bg-background/70 p-3">
      <p className="text-xs font-medium">Tax &amp; accounting</p>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <FieldLabel>Tax line type</FieldLabel>
          <Select
            value={selectedTaxLineType}
            // Untaxed is already the whole answer, and the engine reads both
            // fields: leaving a live class selectable beside "not taxable"
            // only invites the pair to contradict each other.
            disabled={!taxable}
            onValueChange={(next) =>
              onChange({ taxLineType: next === AUTO_TAX_LINE_TYPE ? null : next })
            }
          >
            <SelectTrigger className="h-9 bg-background text-xs">
              {selectedTaxLineType === AUTO_TAX_LINE_TYPE ? (
                <span>
                  Auto <span className="text-muted-foreground">→ {autoLabel}</span>
                </span>
              ) : (
                <span>{taxLineTypeLabel(selectedTaxLineType)}</span>
              )}
            </SelectTrigger>
            {/* Exactly what the engine will honour, and nothing else.
                A line's class is derived from what the line IS, so offering
                `labor`, `materials` or `flat_rate` would be three controls that
                change nothing: pick `labor` on a material and it is still
                classified materials. `exempt` is listed as well as forced by
                unticking Taxable — a catalog line can arrive carrying it beside
                `taxable: true`, and a value this can show but not re-select is
                a trap.

                position="popper" is load-bearing: this trigger renders its own
                label spans instead of a SelectValue, and the default
                item-aligned mode positions relative to the SelectValue node —
                without one the open list never places or receives focus. */}
            <SelectContent position="popper">
              <SelectItem value={AUTO_TAX_LINE_TYPE}>Auto → {autoLabel}</SelectItem>
              {OVERRIDABLE_TAX_LINE_TYPES.map((value) => (
                <SelectItem key={value} value={value}>
                  {taxLineTypeLabel(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] leading-tight text-muted-foreground">
            Derived from the line type. Freight is the exception.
          </p>
        </div>

        <div className="space-y-1.5">
          <FieldLabel>Tax category</FieldLabel>
          <Input
            value={value.taxCategory ?? ''}
            onChange={(event) => onChange({ taxCategory: event.target.value })}
            placeholder="DEFAULT"
            className="h-9 bg-background text-xs"
          />
        </div>

        <div className="space-y-1.5">
          <FieldLabel>Tax code</FieldLabel>
          <Input
            value={value.taxCode ?? ''}
            onChange={(event) => onChange({ taxCode: event.target.value })}
            placeholder="e.g. TX-001"
            className="h-9 bg-background font-mono text-xs"
          />
        </div>
      </div>

      {qbConnected ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <FieldLabel>GL income</FieldLabel>
            <QbAccountPicker
              accounts={qbAccounts}
              accountTypes={['Income']}
              value={value.glAccount ?? undefined}
              onChange={(next) => onChange({ glAccount: next ?? null })}
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>GL COGS</FieldLabel>
            <QbAccountPicker
              accounts={qbAccounts}
              accountTypes={['Cost of Goods Sold']}
              value={value.glCogs ?? undefined}
              onChange={(next) => onChange({ glCogs: next ?? null })}
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>GL expense</FieldLabel>
            <QbAccountPicker
              accounts={qbAccounts}
              accountTypes={['Expense', 'Other Expense', 'Cost of Goods Sold']}
              value={value.glExpenseAccount ?? undefined}
              onChange={(next) => onChange({ glExpenseAccount: next ?? null })}
            />
          </div>
          <div className="space-y-1.5">
            <FieldLabel>GL class</FieldLabel>
            <QbClassPicker
              classes={qbClasses}
              value={value.glClass ?? undefined}
              onChange={(next) => onChange({ glClass: next ?? null })}
            />
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Connect QuickBooks to map GL accounts.{' '}
          <Link
            to="/dashboard/settings/accounting"
            className="font-medium text-primary hover:underline"
          >
            Settings → Accounting
          </Link>
        </p>
      )}
    </div>
  );
}
